import { useEffect, useState, type ChangeEvent, type FormEvent } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import api, { ApiError } from '../lib/apiClient';
import { listOptions, useConfig } from '../context/ConfigContext';
import SearchableSelect from '../components/SearchableSelect';

interface CounterpartyOption {
  id: number;
  name: string;
  role: string;
  [key: string]: unknown;
}

interface InvestmentFormState {
  code: string;
  name: string;
  asset_class: string;
  amount: string;
  currency_code: string;
  expected_return_rate: string;
  acquisition_date: string;
  maturity_date: string;
  status: string;
  notes: string;
  counterparty_id: number | '';
  lease_start_date: string;
  lease_end_date: string;
  rent_review_date: string;
  insurance_expiry_date: string;
  interest_rate_type: string;
  repayment_frequency: string;
  income_frequency: string;
  security_description: string;
  security_value: string;
  coupon_rate: string;
  face_value: string;
  bank_name: string;
  is_rollover: boolean;
  ownership_percent: string;
  is_listed: boolean;
  units_held: string;
  country: string;
}

const emptyForm: InvestmentFormState = {
  code: '',
  name: '',
  asset_class: '',
  amount: '',
  currency_code: 'VUV',
  expected_return_rate: '',
  acquisition_date: '',
  maturity_date: '',
  status: 'active',
  notes: '',
  counterparty_id: '',
  lease_start_date: '',
  lease_end_date: '',
  rent_review_date: '',
  insurance_expiry_date: '',
  interest_rate_type: '',
  repayment_frequency: '',
  income_frequency: '',
  security_description: '',
  security_value: '',
  coupon_rate: '',
  face_value: '',
  bank_name: '',
  is_rollover: false,
  ownership_percent: '',
  is_listed: false,
  units_held: '',
  country: '',
};

interface InvestmentRecord {
  code: string | null;
  name: string;
  asset_class: string;
  amount: number;
  currency_code: string;
  expected_return_rate: number | null;
  acquisition_date: string;
  maturity_date: string | null;
  status: string;
  notes: string | null;
  counterparty_id: number | null;
  lease_start_date: string | null;
  lease_end_date: string | null;
  rent_review_date: string | null;
  insurance_expiry_date: string | null;
  interest_rate_type: string | null;
  repayment_frequency: string | null;
  income_frequency: string | null;
  security_description: string | null;
  security_value: number | null;
  coupon_rate: number | null;
  face_value: number | null;
  bank_name: string | null;
  is_rollover: boolean;
  ownership_percent: number | null;
  is_listed: boolean;
  units_held: number | null;
  country: string | null;
}

interface CurrencyOption {
  currency_code: string;
}

type FormErrors = Record<string, string[]>;

// The counterparty role a given asset class cares about — Property leases
// a tenant, Loans have a borrower. Other classes don't show the picker at
// all (a bond issuer or a bank isn't a tracked counterparty here).
const COUNTERPARTY_ROLE: Record<string, { role: string; label: string }> = {
  property: { role: 'tenant', label: 'Tenant' },
  loans: { role: 'borrower', label: 'Borrower' },
};

export function InvestmentForm() {
  const { lists } = useConfig();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isNew = !id;
  const [form, setForm] = useState<InvestmentFormState>(emptyForm);
  const [currencies, setCurrencies] = useState<string[]>([]);
  const [errors, setErrors] = useState<FormErrors>({});
  const [loaded, setLoaded] = useState(isNew);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get<CurrencyOption[]>('/exchange-rates').then((res) => setCurrencies(res.data.map((r) => r.currency_code)));
  }, []);

  useEffect(() => {
    if (isNew) return;
    api.get<InvestmentRecord>(`/investments/${id}`).then((res) => {
      const investment = res.data;
      setForm({
        code: investment.code ?? '',
        name: investment.name,
        asset_class: investment.asset_class,
        amount: String(investment.amount),
        currency_code: investment.currency_code,
        expected_return_rate: investment.expected_return_rate != null ? String(investment.expected_return_rate) : '',
        acquisition_date: investment.acquisition_date,
        maturity_date: investment.maturity_date ?? '',
        status: investment.status,
        notes: investment.notes ?? '',
        counterparty_id: investment.counterparty_id ?? '',
        lease_start_date: investment.lease_start_date ?? '',
        lease_end_date: investment.lease_end_date ?? '',
        rent_review_date: investment.rent_review_date ?? '',
        insurance_expiry_date: investment.insurance_expiry_date ?? '',
        interest_rate_type: investment.interest_rate_type ?? '',
        repayment_frequency: investment.repayment_frequency ?? '',
        income_frequency: investment.income_frequency ?? '',
        security_description: investment.security_description ?? '',
        security_value: investment.security_value != null ? String(investment.security_value) : '',
        coupon_rate: investment.coupon_rate != null ? String(investment.coupon_rate) : '',
        face_value: investment.face_value != null ? String(investment.face_value) : '',
        bank_name: investment.bank_name ?? '',
        is_rollover: investment.is_rollover,
        ownership_percent: investment.ownership_percent != null ? String(investment.ownership_percent) : '',
        is_listed: investment.is_listed,
        units_held: investment.units_held != null ? String(investment.units_held) : '',
        country: investment.country ?? '',
      });
      setLoaded(true);
    });
  }, [id, isNew]);

  const set =
    (key: keyof InvestmentFormState) =>
    (e: ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) =>
      setForm({ ...form, [key]: e.target.value });

  const setChecked = (key: keyof InvestmentFormState) => (e: ChangeEvent<HTMLInputElement>) =>
    setForm({ ...form, [key]: e.target.checked });

  const save = async (e: FormEvent) => {
    e.preventDefault();
    if (saving) return;
    setSaving(true);
    const payload: Record<string, unknown> = {
      ...form,
      counterparty_id: form.counterparty_id || undefined,
      maturity_date: form.maturity_date || undefined,
      expected_return_rate: form.expected_return_rate || undefined,
      lease_start_date: form.lease_start_date || undefined,
      lease_end_date: form.lease_end_date || undefined,
      rent_review_date: form.rent_review_date || undefined,
      insurance_expiry_date: form.insurance_expiry_date || undefined,
      interest_rate_type: form.interest_rate_type || undefined,
      repayment_frequency: form.repayment_frequency || undefined,
      income_frequency: form.income_frequency || undefined,
      security_description: form.security_description || undefined,
      security_value: form.security_value || undefined,
      coupon_rate: form.coupon_rate || undefined,
      face_value: form.face_value || undefined,
      bank_name: form.bank_name || undefined,
      ownership_percent: form.ownership_percent || undefined,
      units_held: form.units_held || undefined,
      country: form.country || undefined,
    };
    try {
      if (isNew) await api.post('/investments', payload);
      else await api.put(`/investments/${id}`, payload);
      navigate('/investments');
    } catch (err) {
      setErrors(
        err instanceof ApiError && err.errors ? err.errors : { general: [err instanceof ApiError ? err.message : 'Save failed.'] }
      );
    } finally {
      setSaving(false);
    }
  };

  if (!loaded) return null;

  const counterparty = COUNTERPARTY_ROLE[form.asset_class];

  return (
    <>
      <header className="page-header">
        <h1>{isNew ? 'Add investment' : 'Edit investment'}</h1>
      </header>

      <div className="card">
        <form onSubmit={save} className="form-grid">
          {errors.general && <div className="alert alert-error span-2">{errors.general[0]}</div>}
          {!isNew && (
            <label>
              Reference code
              <input value={form.code} disabled />
            </label>
          )}
          <label className="span-2">
            Name *
            <input type="text" value={form.name} onChange={set('name')} required />
            {errors.name && <small className="error">{errors.name[0]}</small>}
          </label>
          <label className="span-2">
            Asset class *
            <select value={form.asset_class} onChange={set('asset_class')} required>
              <option value="">Select…</option>
              {listOptions(lists, 'investment_asset_class').map((item) => (
                <option key={item.value} value={item.value}>
                  {item.label}
                </option>
              ))}
            </select>
            {errors.asset_class && <small className="error">{errors.asset_class[0]}</small>}
          </label>
          <label>
            Amount *
            <input type="number" step="0.01" min="0" value={form.amount} onChange={set('amount')} required />
            {errors.amount && <small className="error">{errors.amount[0]}</small>}
          </label>
          <label>
            Currency
            <select value={form.currency_code} onChange={set('currency_code')}>
              <option value="VUV">VUV</option>
              {currencies.map((code) => (
                <option key={code} value={code}>
                  {code}
                </option>
              ))}
            </select>
            {errors.currency_code && <small className="error">{errors.currency_code[0]}</small>}
          </label>
          <label>
            Expected return (%)
            <input
              type="number"
              step="0.01"
              min="0"
              max="100"
              value={form.expected_return_rate}
              onChange={set('expected_return_rate')}
            />
            {errors.expected_return_rate && <small className="error">{errors.expected_return_rate[0]}</small>}
          </label>
          <label>
            Acquisition date *
            <input type="date" value={form.acquisition_date} onChange={set('acquisition_date')} required />
            {errors.acquisition_date && <small className="error">{errors.acquisition_date[0]}</small>}
          </label>
          <label>
            Maturity date
            <input type="date" value={form.maturity_date} onChange={set('maturity_date')} />
            {errors.maturity_date && <small className="error">{errors.maturity_date[0]}</small>}
          </label>
          {!isNew && (
            <label>
              Status
              <select value={form.status} onChange={set('status')}>
                <option value="active">Active</option>
                <option value="vacant">Vacant</option>
                <option value="matured">Matured</option>
                <option value="demolished">Demolished</option>
                <option value="closed">Closed</option>
                {form.status === 'disposed' && <option value="disposed">Disposed</option>}
              </select>
              {form.status !== 'disposed' && (
                <small className="muted">Disposal is a separate, approved action from the investment&apos;s detail page.</small>
              )}
            </label>
          )}

          {counterparty && (
            <label>
              {counterparty.label}
              <SearchableSelect<CounterpartyOption>
                endpoint="/investment-counterparties"
                value={form.counterparty_id}
                onChange={(value) => setForm({ ...form, counterparty_id: Number(value) })}
                getLabel={(c) => c.name}
                params={{ role: counterparty.role }}
                placeholder={`Search ${counterparty.label.toLowerCase()}s…`}
              />
              {errors.counterparty_id && <small className="error">{errors.counterparty_id[0]}</small>}
            </label>
          )}

          {form.asset_class === 'property' && (
            <>
              <label>
                Lease start
                <input type="date" value={form.lease_start_date} onChange={set('lease_start_date')} />
              </label>
              <label>
                Lease end
                <input type="date" value={form.lease_end_date} onChange={set('lease_end_date')} />
              </label>
              <label>
                Rent review date
                <input type="date" value={form.rent_review_date} onChange={set('rent_review_date')} />
              </label>
              <label>
                Insurance expiry
                <input type="date" value={form.insurance_expiry_date} onChange={set('insurance_expiry_date')} />
              </label>
              <label>
                Rent frequency
                <select value={form.income_frequency} onChange={set('income_frequency')}>
                  <option value="">Select…</option>
                  {listOptions(lists, 'investment_repayment_frequency').map((item) => (
                    <option key={item.value} value={item.value}>
                      {item.label}
                    </option>
                  ))}
                </select>
              </label>
            </>
          )}

          {form.asset_class === 'loans' && (
            <>
              <label>
                Interest rate type
                <select value={form.interest_rate_type} onChange={set('interest_rate_type')}>
                  <option value="">Select…</option>
                  {listOptions(lists, 'investment_interest_rate_type').map((item) => (
                    <option key={item.value} value={item.value}>
                      {item.label}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                Repayment frequency
                <select value={form.repayment_frequency} onChange={set('repayment_frequency')}>
                  <option value="">Select…</option>
                  {listOptions(lists, 'investment_repayment_frequency').map((item) => (
                    <option key={item.value} value={item.value}>
                      {item.label}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                Security value
                <input type="number" step="0.01" min="0" value={form.security_value} onChange={set('security_value')} />
              </label>
              <label className="span-2">
                Security description
                <textarea rows={2} value={form.security_description} onChange={set('security_description')} />
              </label>
            </>
          )}

          {form.asset_class === 'fixed_interest' && (
            <>
              <label>
                Coupon rate (%)
                <input type="number" step="0.01" min="0" max="100" value={form.coupon_rate} onChange={set('coupon_rate')} />
              </label>
              <label>
                Face value
                <input type="number" step="0.01" min="0" value={form.face_value} onChange={set('face_value')} />
              </label>
              <label>
                Coupon frequency
                <select value={form.income_frequency} onChange={set('income_frequency')}>
                  <option value="">Select…</option>
                  {listOptions(lists, 'investment_repayment_frequency').map((item) => (
                    <option key={item.value} value={item.value}>
                      {item.label}
                    </option>
                  ))}
                </select>
              </label>
            </>
          )}

          {form.asset_class === 'cash' && (
            <>
              <label>
                Bank name
                <input value={form.bank_name} onChange={set('bank_name')} />
              </label>
              <label className="checkbox-inline">
                <input type="checkbox" checked={form.is_rollover} onChange={setChecked('is_rollover')} />
                Rolls over at maturity
              </label>
            </>
          )}

          {form.asset_class === 'equity' && (
            <>
              <label>
                Ownership (%)
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  max="100"
                  value={form.ownership_percent}
                  onChange={set('ownership_percent')}
                />
              </label>
              <label className="checkbox-inline">
                <input type="checkbox" checked={form.is_listed} onChange={setChecked('is_listed')} />
                Listed
              </label>
              <label>
                Units held
                <input type="number" step="0.0001" min="0" value={form.units_held} onChange={set('units_held')} />
              </label>
            </>
          )}

          {form.asset_class === 'offshore' && (
            <label>
              Country
              <input value={form.country} onChange={set('country')} />
            </label>
          )}

          <label className="span-2">
            Notes
            <textarea rows={3} value={form.notes} onChange={set('notes')} />
          </label>
          <div className="form-actions span-2">
            <Link className="btn btn-ghost" to="/investments">
              Cancel
            </Link>
            <button className="btn btn-primary" disabled={saving}>
              {saving ? 'Saving…' : 'Save'}
            </button>
          </div>
        </form>
      </div>
    </>
  );
}
